import {
  Box,
  Divider,
  List,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";

import { QuestionInterfaceProps, VoteFieldValidation } from "../types";
import { CandidateRow } from "../../components/candidate-row";
import { Loading } from "../../components/loading";
import { ICandidate } from "../../domain/candidate";
import { PREFERENCE, PreferenceVote } from "./preference-typedef";
import { CANDIDATE_DISPLAY_FORMAT } from "../../domain/ballot-api";
import { IQuestionState } from "../../domain/ballot-state";
import { shuffleCandidates } from "../../util/shuffle_candidates";

export const QuestionPreference = ({ setQuestionState, question, questionState }: QuestionInterfaceProps<PreferenceVote>) => {
  const { invalid_reasons } = questionState || {};

  const value = PREFERENCE.normalize(questionState);

  const candidates = useMemo(() => {
    if (question && question.candidates) {
      const _candidates = Object.values(question.candidates);
      if (question.shuffle_candidates) {
        return shuffleCandidates(_candidates)
      }
      return _candidates;
    }
  }, [question]);

  const setQuestionVote = useCallback(
    (value: React.SetStateAction<any>) => {
      setQuestionState((response: IQuestionState) => {
        const _value = typeof value === "function" ? value(response.value) : value;

        const validation = PREFERENCE.validate(question, _value);

        return {
          question_id: question.id,
          response_type: PREFERENCE.TYPE,
          valid: validation.valid,
          invalid_reasons: validation.reasons,
          data_version: "1.0.0",
          dirty: true,
          value: _value
        };
      });
    },
    [setQuestionState, question]
  );

  const setCandidateRank = useCallback(
    (candidate: ICandidate, rank: number | null) => {
      setQuestionVote((pv: PreferenceVote): PreferenceVote => {
        return {
          ...pv,
          [candidate.id]: {
            id: candidate.id,
            index: rank,
            label: candidate.label,
          },

        };
      });
    },
    [setQuestionVote]
  );

  const { candidate_display_format } = question;

  const getRank = useCallback(
    (id: string) => (value && value[id] ? value[id].index : null),
    [value]
  );

  const ranks = useMemo(() => {
    if (!candidates) return [];

    return candidates.map((c) => getRank(c.id));
  }, [candidates, getRank]);

  const getNextNumber = useCallback(
    (start: number = 1) => {
      if (!candidates) {
        return 0;
      }
      const max = candidates.length;
      if (start === max) {
        if (max !== 1) {
          return null;
        }
      }
      for (let i = start; i <= max; i++) {
        if (!ranks.includes(i)) {
          return i;
        }
      }
      return max;
    },
    [candidates, ranks]
  );

  const handleClick = useCallback(
    (c: ICandidate) => {
      const current = getRank(c.id);

      setCandidateRank(
        c,
        getNextNumber(current !== null ? current : 1)
      );
    },
    [getRank, getNextNumber, setCandidateRank]
  );

  useEffect(() => {
    console.log("handleClick updated");
  }, [handleClick]);

  if (!candidates) {
    return (
      <Loading source="PreferenceTextInterface" waitingFor={{ candidates }} />
    );
  }

  return (
    <Box pt={2}>
      <List disablePadding>
        {candidates.map((c, i) => (
          (<CandidatePreferenceRow
            key={c.id}
            candidate={c}
            rank={getRank(c.id)}
            errors={invalid_reasons ? invalid_reasons.filter(r => r.target === `candidate_${c.id}`) : undefined}
            onClick={handleClick}
            onChange={setCandidateRank}
            display_format={candidate_display_format}
          />)


        ))}
        <Divider component="li" />
      </List>
    </Box>
  );
};


interface CandidatePreferenceRowProps {
  rank: string | number | null;
  errors?: VoteFieldValidation[];
  onClick: (c: ICandidate) => void;
  onChange: (c: ICandidate, rank: number | null) => void;
  candidate: ICandidate;
  display_format?: CANDIDATE_DISPLAY_FORMAT
}
export const CandidatePreferenceRow = (props: CandidatePreferenceRowProps) => {
  const { rank, onChange, candidate, onClick, display_format, errors } = props;
  const handleInput = useCallback(
    (e: any) => {
      const raw = e.target.value;
      if (raw === "" || raw === null) {
        return onChange(candidate, null);
      }
      let value = Number(raw);
      if (value <= 0) {
        value = 1;
      }

      onChange(candidate, value);
    },
    [candidate, onChange]
  );
  const error = errors && errors.length > 0;
  const handleClick = useCallback(() => onClick(candidate), [onClick, candidate])
  const icon = useMemo(() => {
    return (<TextField
      sx={{
        width: 30,
        justifyContent: "center",
        justifyItems: "center",
        alignContent: "center",
        alignItems: "center",
        marginRight: 2,
        cursor: "pointer",
      }}
      value={rank || ""}
      onChange={handleInput}
      error={error}
      color={error ? "error" : undefined}
      size="small"
      inputProps={{
        style: {
          justifyContent: "center",
          justifyItems: "center",
          alignContent: "center",
          alignItems: "center",
          display: "flex",
          cursor: "pointer",
          textAlign: "center",
          padding: 0,
          color: error ? "#e8002e" : undefined
        },
      }}
      onFocus={(event) => {
        event.target.select();
      }}
    />)
  }, [rank, handleInput, error]);
  return <CandidateRow
    candidate={candidate}
    display_format={display_format}
    icon={icon}
    onClick={handleClick} />

}
